// hubSuppliers.js
import Vue from 'vue'
import gql from 'graphql-tag'
import { apolloClient } from '@/apollo'
import axios from 'axios'
import config from '@/config'
import { AGGREGATED_TITLES, VALIDATION_STATUS, USAGE_STEPS_TYPES, SEND_CREDENTIALS_STEPS, validateDataAreComplete } from '@/utils/validationSteps'

const getSupplierRegister = gql`
  query getSupplierRegister(
    $limit: Int
    $offset: Int
    $company_name: String
    $buyer_name_id: Int
    $country: String
  ) {
    hub_supplier_register_aggregate(
      where: {
        buyer_name_id: { _eq: $buyer_name_id }
        country: { _eq: $country }
        _or: [
          { company_name: { _ilike: $company_name } }
          { tax_id: { _ilike: $company_name } }
        ]
      }
    ) {
      aggregate { count }
      }
    hub_supplier_register(
      where: {
        buyer_name_id: { _eq: $buyer_name_id }
        country: { _eq: $country }
        _or: [
          { company_name: { _ilike: $company_name } }
          { tax_id: { _ilike: $company_name } }
        ]
      }
      order_by: { created_at: desc }
      limit: $limit
      offset: $offset
    ) {
      id
      buyer_name
      tax_id
      company_name
      contact_name
      contact_lastname
      country
      buyer_name_id
      created_at
      finalized_at
      steps: supplier_steps(
        order_by: { validated_at: desc },
        limit: 1,
        where: { validated_at: { _is_null: false } }
      ) {
        id
        step_type
        message
        priority
        status
        validated_at
        register_id
      }
    }
  }
`

const getCountries = gql`
  query getCountries {
    country(order_by: { name: asc }) {
      id
      name
      code
      show
    }
  }
`

const getRegisterCountriesDistinct = gql`
  query getRegisterCountriesDistinct {
    hub_supplier_register(distinct_on: country, order_by: { country: asc }) {
      country
    }
  }
`

const getSupplierStats = gql`
  query getSupplierStats($todayStart: timestamptz!, $tomorrowStart: timestamptz!) {
    total: hub_supplier_register_aggregate { aggregate { count } }
    today: hub_supplier_register_aggregate(
      where: { created_at: { _gte: $todayStart, _lt: $tomorrowStart } }
    ) { aggregate { count } }
    topSample: hub_supplier_register(limit: 1000, order_by: { created_at: desc }) {
      buyer_name
    }
  }
`

const getPlanTypes = gql`
  query getPlanTypes {
    plan_type(where: { is_active: { _eq: true } }, order_by: { name: asc }) {
      id
      name
      description
      is_active
    }
  }
`

const getStepsMeta = gql`
  query getStepsMeta {
    hub_step(where: { is_active: { _eq: true } }, order_by: { order_to_show: asc }) {
      id
      name
      title
      is_active
      is_creation_step
      is_unified
      have_revalidate
      allow_create
      allow_update
      allow_unlock
      allow_send_credentials
      order_to_show
    }
  }
`

const getSupplierById = gql`
  query getSupplierById($id: Int!) {
    hub_supplier_register(where: { id: { _eq: $id } }, limit: 1) {
      id
      buyer_name_id
      buyer_name
      tax_id
      company_name
      contact_name
      contact_lastname
      country
      contact_legal_email
      contact_legal_lastname
      contact_legal_name
      contact_legal_notification
      contact_legal_phone
      contact_legal_position
      contact_notification
      contact_phone
      contact_position
      contract_end_date
      contract_start_date
      customer_type
      finalized_by
      marketplace_timezone_id
      contact_email
      company_web_site_url
      company_phone
      company_legal_name
      company_business
      company_address
      acuseller_plan_type_id
      created_at
      finalized_at
      steps: supplier_steps(order_by: [{ step_type: asc }, { validated_at: desc }, { id: desc }]) {
        id
        step_type
        message
        priority
        status
        validated_at
        register_id
      }
    }
  }
`

const getMiningTaxonomies = gql`
  query getMiningTaxonomies {
    taxonomy(where: { description: { _ilike: "%mineria%" } }) {
      id
      name
    }
  }
`

const getPublicCatalogTypes = gql`
  query getPublicCatalogTypes {
    catalog_type(where: { is_public: { _eq: true }, is_visible: { _eq: true }, name: { _ilike: "%global%" } }) {
      id
    }
  }
`

const getCustomerTypes = gql`
  query getCustomerTypes {
    hub_selector_values(
      where: { is_active: { _eq: true }, selector_name: { _eq: "customer_type" } }
      order_by: { name: asc }
    ) {
      id
      name
      description
    }
  }
`

const getOrganizationTermsAndConditions = gql`
  query getOrganizationTermsAndConditions($taxId: String!) {
    organization_accepted_terms(where: {organization: {tax_id: {_eq: $taxId}}}) {
      acceptance_date
      user {
        email
      }
    }
  }
`

const finalizeSupplier = gql`
  mutation finalizeSupplier($id: Int!, $finalized_at: timestamptz!, $finalized_by: String!) {
    update_hub_supplier_register_by_pk(pk_columns: { id: $id }, _set: { finalized_at: $finalized_at, finalized_by: $finalized_by }) {
      id
    }
  }
`

const insertSupplier = gql`
  mutation insertSupplier($data: hub_supplier_register_insert_input!) {
    insert_hub_supplier_register_one(object: $data) {
      id
      steps: supplier_steps(order_by: [{ step_type: asc }, { validated_at: desc }, { id: desc }]) {
        id
        step_type
        message
        priority
        status
        validated_at
        register_id
      }
    }
  }
`

const updateSupplier = gql`
  mutation updateSupplier($id: Int!, $data: hub_supplier_register_set_input!) {
    update_hub_supplier_register_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`

const updateSupplierStep = gql`
  mutation updateSupplierStep($id: Int!, $validated_at: timestamptz!, $status: smallint!, $message: String!) {
    update_hub_supplier_step_by_pk(pk_columns: {id: $id}, _set: {validated_at: $validated_at, status: $status, message: $message}) {
      id
    }
  }
`

const createCat4mroOrganization = gql`
  mutation createCat4mroOrganization($data: organization_insert_input!) {
    insert_organization_one(object: $data) {
      id
    }
  }
`

const updateOrganizationName = gql`
  mutation updateOrganizationName($taxId: String!, $name: String!) {
    update_organization(where: {tax_id: {_eq: $taxId}}, _set: {name: $name}) {
      returning {
        id
      }
    }
  }
`

const updateOrganizationLocked = gql`
  mutation updateOrganizationLocked($taxId: String!, $locked: Boolean!) {
    update_organization(where: {tax_id: {_eq: $taxId}}, _set: {is_blocked_by_contract: $name}) {
      returning {
        id
      }
    }
  }
`

const canAddNewUsersValidation = gql`
  query canAddNewUsersValidation(
    $_in: [String!] = [
      "tax_id_cat4mro_validation"
      "tax_id_marketplace_validation"
    ]
    $taxID: String!
  ) {
    hub_supplier_step(
      where: {
        step_type: { _in: $_in }
        supplier_register: { tax_id: { _eq: $taxID } }
      }
    ) {
      id
      message
      status
      validated_at
    }
  }
`

async function updateStep (step, status, message) {
  try {
    await apolloClient.mutate({
      mutation: updateSupplierStep,
      variables: {
        id: Number(step.id),
        validated_at: new Date().toISOString(),
        status,
        message
      }
    })
    return { success: status === VALIDATION_STATUS.SUCCESS, message }
  } catch (error) {
    console.error('Error updating supplier step:', error)
    throw error
  }
}

const hubSuppliers = {
  namespaced: true,
  state: {
    suppliers: [],
    suppliersCount: 0,
    statistics: {
      todayRecords: 0,
      totalRecords: 0,
      topBuyer: { buyer_name: '', count: 0 }
    },
    buyers: [],
    countries: [],
    timezones: [],
    planTypes: [],
    customerTypes: [],
    validationSteps: [],
    supplierOverview: {},
    selectedSupplier: null,
    creationState: { can_create: false, required_creations: [], message: '' },
    unlockState: { can_unlock: false, unlock_disabled: [] },
    canSendCredentials: false,
    registerCountriesDistinct: [],
    stepsMeta: [],
    miningTaxonomies: [],
    publicCatalogTypes: [],
    nextCursor: null,
    buyerCompanies: [],
    buyerCompaniesTotal: 0,
    services: [],
    allServices: []
  },
  actions: {
    async fetchBuyers ({ commit, state }) {
      if (state.buyers && state.buyers.length) return state.buyers
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/buyers`
        const { data } = await axios.get(url)
        const list = Array.isArray(data) ? data : []
        commit('SET_BUYERS', list)
        return list
      } catch (error) {
        console.error('Error fetching buyers (REST):', error)
        commit('SET_BUYERS', [])
        return []
      }
    },
    async fetchBuyerCompaniesActive ({ commit, state }) {
      if (state.buyers && state.buyers.length) return state.buyers
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/buyer-companies?active=true`
        const { data } = await axios.get(url)
        const list = Array.isArray(data.items) ? data.items : []
        commit('SET_BUYERS', list)
        return list
      } catch (error) {
        console.error('Error fetching buyers (REST):', error)
        commit('SET_BUYERS', [])
        return []
      }
    },
    async fetchBuyersRelations ({ state }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/buyers-data-relations`
        const { data } = await axios.get(url)
        const list = Array.isArray(data) ? data : []
        return list
      } catch (error) {
        console.error('Error fetching buyers (REST):', error)
        return []
      }
    },
    async fetchSuppliersWithoutTraceability ({ commit, state }, variables = {}) {
      try {
        const params = {
          take: variables.take || 10
        }
        if (variables.company_name) params.company_name = variables.company_name
        if (variables.country) params.country = variables.country
        if (variables.cursor) params.cursor = variables.cursor

        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/without-traceability`
        const { data } = await axios.get(url, { params })

        const mapped = (data.items || []).map(item => ({
          id: item.id,
          buyer_name: null,
          tax_id: item.tax_id || '',
          company_name: item.company_name || '',
          contact_name: item.contact_name || '',
          contact_lastname: item.contact_lastname || '',
          country: item.country || '',
          buyer_name_id: null,
          created_at: item.created_at || null,
          finalized_at: null,
          steps: [],
          last_step_status: null,
          last_step_message: 'Sin trazabilidad',
          general_status: null,
          general_status_message: 'Sin trazabilidad',
          hasTraceability: false
        }))

        if (!variables.cursor) {
          commit('SET_NEXT_CURSOR', null)
          commit('SET_SUPPLIERS', mapped)
          commit('SET_SUPPLIERS_COUNT', data.total || 0)
        } else {
          commit('APPEND_SUPPLIERS', mapped)
        }

        commit('SET_NEXT_CURSOR', data.next_cursor)
        return { mapped, next_cursor: data.next_cursor }
      } catch (error) {
        console.error('Error fetching suppliers without traceability:', error)
        throw error
      }
    },
    async fetchSupplierWithoutTraceabilityByCompanyName ({ commit }, companyNameId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/without-traceability/${companyNameId}`
        const { data } = await axios.get(url)

        if (!data) return null

        const normalized = {
          id: data.id,
          buyer_name_id: data.buyerNameId,
          buyer_name: data.buyerName,
          tax_id: data.taxId,
          company_name: data.companyName,
          contact_name: data.contactName,
          contact_lastname: data.contactLastName,
          country: data.country,
          contact_legal_email: data.contactLegalEmail,
          contact_legal_lastname: data.contactLegalLastName,
          contact_legal_name: data.contactLegalName,
          contact_legal_notification: data.contactLegalNotification,
          contact_legal_phone: data.contactLegalPhone,
          contact_legal_position: data.contactLegalPosition,
          contact_notification: data.contactNotification,
          contact_phone: data.contactPhone,
          contact_position: data.contactPosition,
          contract_end_date: data.contractEndDate,
          contract_start_date: data.contractStartDate,
          customer_type: data.customerType,
          finalized_by: data.finalizedBy,
          marketplace_timezone_id: data.marketplaceTimezoneId,
          contact_email: data.contactEmail,
          company_web_site_url: data.companyWebSiteUrl,
          company_phone: data.companyPhone,
          company_legal_name: data.companyLegalName,
          company_business: data.companyBusiness,
          company_address: data.companyAddress,
          acuseller_plan_type_id: data.acusellerPlanTypeId,
          created_at: data.createdAt,
          finalized_at: data.finalizedAt,
          users_associate: data.usersAssociate,
          steps: data.steps,
          last_step_status: null,
          last_step_message: 'Sin trazabilidad',
          general_status: null,
          general_status_message: 'Sin trazabilidad',
          hasTraceability: false,
          is_legal_representative: data.isLegalRepresentative
        }

        commit('SET_SELECTED_SUPPLIER', normalized)

        return normalized
      } catch (error) {
        console.error('Error fetching supplier without traceability by taxId:', error)
        return null
      }
    },
    async fetchAllSuppliers ({ commit, state, dispatch }, variables = {}) {
      try {
        let whereCountry = variables.country || null
        if (whereCountry) {
          if (!state.countries || state.countries.length === 0) {
            await dispatch('fetchCountries')
          }
          if (!state.registerCountriesDistinct || state.registerCountriesDistinct.length === 0) {
            await dispatch('fetchRegisterCountriesDistinct')
          }

          const found = state.registerCountriesDistinct.find(entry =>
            entry.country && entry.country.toLowerCase() === whereCountry.toLowerCase()
          )

          if (found) {
            whereCountry = found.country
          } else {
            whereCountry = null
          }
        }

        let response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getSupplierRegister,
          variables: {
            limit: variables.take || 10,
            offset: variables.skip || 0,
            company_name: variables.company_name ? `%${variables.company_name}%` : null,
            buyer_name_id: variables.buyer_name_id ? Number(variables.buyer_name_id) : null,
            country: whereCountry
          }
        })

        if (response && response.data) {
          const raw = response.data.hub_supplier_register || []
          const mapped = raw.map(item => {
            const steps = (item.steps || []).reduce((acc, st) => {
              const key = st.step_type
              if (!acc[key]) acc[key] = st
              return acc
            }, {})
            const stepsList = Object.values(steps)

            let last = null
            for (const st of stepsList) {
              if (!last) { last = st; continue }
              const a = last.validated_at || ''
              const b = st.validated_at || ''
              if ((b && !a && b) || (a && b && b > a) || (!a && !b && st.id > last.id)) {
                last = st
              }
            }

            const generalStatus = last ? last.status : 0
            const generalStatusMessage = last ? (last.message || '') : 'Pendiente'

            return {
              ...item,
              steps: stepsList,
              last_step_status: generalStatus,
              last_step_message: generalStatusMessage,
              general_status: generalStatus,
              general_status_message: generalStatusMessage
            }
          })

          commit('SET_SUPPLIERS', mapped)
          commit('SET_SUPPLIERS_COUNT', response.data.hub_supplier_register_aggregate.aggregate.count)
          return { suppliers: mapped, suppliers_aggregate: response.data.hub_supplier_register_aggregate }
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error)
        throw error
      }
    },
    async fetchStepsMeta ({ commit, state }) {
      if (state.stepsMeta && state.stepsMeta.length) return state.stepsMeta
      try {
        const resp = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getStepsMeta
        })
        const list = (resp && resp.data && resp.data.hub_step) || []
        commit('SET_STEPS_META', list)
        return list
      } catch (error) {
        console.error('Error fetching steps meta:', error)
        commit('SET_STEPS_META', [])
        return []
      }
    },
    async fetchCountries ({ commit }) {
      try {
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getCountries
        })
        if (response && response.data && response.data.country) {
          const normalizedCountries = response.data.country.map(country => ({
            ...country,
            name: country.name
              .replace(/\u00A0/g, ' ') // NBSP → espacio normal
              .replace(/\s+/g, ' ')
              .trim(),

            code: country.code ? country.code.trim() : country.code
          }))

          commit('SET_COUNTRIES', normalizedCountries)
          return normalizedCountries
        }
        commit('SET_COUNTRIES', [])
        return []
      } catch (error) {
        console.error('Error fetching countries:', error)
        commit('SET_COUNTRIES', [])
        throw error
      }
    },
    async fetchRegisterCountriesDistinct ({ commit }) {
      try {
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getRegisterCountriesDistinct
        })
        const list = (response && response.data && response.data.hub_supplier_register) || []
        commit('SET_REGISTER_COUNTRIES_DISTINCT', list)
        return list
      } catch (error) {
        console.error('Error fetching register countries distinct:', error)
        commit('SET_REGISTER_COUNTRIES_DISTINCT', [])
        return []
      }
    },
    async fetchTimezones ({ commit, state }) {
      if (state.timezones && state.timezones.length) return state.timezones
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/time-zones`
        const { data } = await axios.get(url)
        const list = Array.isArray(data) ? data : []
        commit('SET_TIMEZONES', list)
        return list
      } catch (error) {
        console.error('Error fetching timezones (REST):', error)
        commit('SET_TIMEZONES', [])
        return []
      }
    },
    async fetchPlanTypes ({ commit, state }) {
      if (state.planTypes && state.planTypes.length) return state.planTypes
      try {
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getPlanTypes
        })
        const list = (response && response.data && response.data.plan_type) || []
        commit('SET_PLAN_TYPES', list)
        return list
      } catch (error) {
        console.error('Error fetching plan types:', error)
        commit('SET_PLAN_TYPES', [])
        return []
      }
    },
    async fetchMiningTaxonomies ({ commit, state }) {
      if (state.miningTaxonomies && state.miningTaxonomies.length) return state.miningTaxonomies
      try {
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getMiningTaxonomies
        })
        const list = (response && response.data && response.data.taxonomy) || []
        commit('SET_MINING_TAXONOMIES', list)
        return list
      } catch (error) {
        console.error('Error fetching mining taxonomies:', error)
        commit('SET_MINING_TAXONOMIES', [])
        return []
      }
    },
    async fetchPublicCatalogTypes ({ commit, state }) {
      if (state.publicCatalogTypes && state.publicCatalogTypes.length) return state.publicCatalogTypes
      try {
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getPublicCatalogTypes
        })
        const list = (response && response.data && response.data.catalog_type) || []
        commit('SET_PUBLIC_CATALOG_TYPES', list)
        return list
      } catch (error) {
        console.error('Error fetching public catalog types:', error)
        commit('SET_PUBLIC_CATALOG_TYPES', [])
        return []
      }
    },
    async fetchCustomerTypes ({ commit, state }) {
      if (state.customerTypes && state.customerTypes.length) return state.customerTypes
      try {
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getCustomerTypes
        })
        const list = (response && response.data && response.data.hub_selector_values) || []
        commit('SET_CUSTOMER_TYPES', list)
        return list
      } catch (error) {
        console.error('Error fetching customer types:', error)
        commit('SET_CUSTOMER_TYPES', [])
        return []
      }
    },
    async canAddNewUsersValidation (_, variables) {
      try {
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: canAddNewUsersValidation,
          variables
        })
        const list = (response && response.data && response.data.hub_supplier_step) || []
        if (!Array.isArray(list) || list.length === 0) return false
        return list.every(item => item.status === 5)
      } catch (error) {
        console.error('Error in canAddNewUsersValidation:', error)
        return false
      }
    },
    async saveSupplier ({ commit }, payload) {
      try {
        if (payload.id) {
          const id = payload.id
          delete payload.id
          delete payload.supplier_steps
          const result = await apolloClient.mutate({
            mutation: updateSupplier,
            variables: { id: Number(id), data: payload }
          })
          const updated = result && result.data && result.data.update_hub_supplier_register_by_pk
          return updated || null
        } else {
          const result = await apolloClient.mutate({
            mutation: insertSupplier,
            variables: { data: payload }
          })
          const inserted = result && result.data && result.data.insert_hub_supplier_register_one
          return inserted || null
        }
      } catch (error) {
        console.error('Error saving supplier:', error)
        throw error
      }
    },
    async updateSupplierWithoutTraceability ({ commit }, { id, payload }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/without-traceability/${id}`
        const { data } = await axios.put(url, payload)
        return data
      } catch (error) {
        console.error('Error updating supplier without traceability:', error)
        throw error
      }
    },
    async updateProviderOnPlatformIfExists ({ commit }, payload) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/sync-provider-platform`
        const { data } = await axios.put(url, payload)
        return data
      } catch (error) {
        console.error('Error updating supplier without traceability:', error)
        throw error
      }
    },
    async resetValidationSteps ({ commit }, { steps, prefix, change }) {
      try {
        const stepsToReset = (steps || []).filter(s => s.id && s.step_type && s.step_type.includes(prefix))

        if (!stepsToReset.length) return []

        await Promise.all(stepsToReset.map(step => {
          const isCredentialsStep = SEND_CREDENTIALS_STEPS.includes(step.step_type)

          return apolloClient.mutate({
            mutation: updateSupplierStep,
            variables: {
              id: Number(step.id),
              validated_at: new Date().toISOString(),
              status: isCredentialsStep ? VALIDATION_STATUS.PENDING : VALIDATION_STATUS.VALIDATION_ERROR,
              message: isCredentialsStep
                ? `El contacto ${change.oldContact} fue modificado por ${change.newContact}. Pendiente de enviar credenciales al nuevo contacto.`
                : `El contacto ${change.oldContact} fue modificado por ${change.newContact}. Pendiente de crear/validar en la plataforma.`
            }
          })
        }))

        return stepsToReset.map(s => s.step_type)
      } catch (error) {
        console.error('Error resetting validation steps:', error)
        throw error
      }
    },
    async massiveUpload ({ commit }, formData) {
      try {
        const response = await axios.post(
          `${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/organization/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        return response
      } catch (e) {
        console.error('UPLOAD FAILURE!!', e)
        throw e
      }
    },
    async massiveTemplate ({ commit }) {
      try {
        const response = await axios.get(
          `${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/organization/massive-template`,
          {
            responseType: 'blob',
            headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          }
        )

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        let fileName = 'Suppliers_Template_' + new Date().toISOString().slice(0, 10) + '.xlsx'
        if (response.headers['content-disposition']) {
          const parts = response.headers['content-disposition'].split('filename=')
          if (parts.length > 1) {
            fileName = parts[1].replace(/"/g, '')
          }
        }

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('UPLOAD FAILURE!!', error)
        throw error
      }
    },
    async massiveTemplateUsers ({ commit }) {
      try {
        const response = await axios.get(
          `${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/user/massive-template`,
          {
            responseType: 'blob',
            headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          }
        )

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        let fileName = 'Users_Template_' + new Date().toISOString().slice(0, 10) + '.xlsx'
        if (response.headers['content-disposition']) {
          const parts = response.headers['content-disposition'].split('filename=')
          if (parts.length > 1) {
            fileName = parts[1].replace(/"/g, '')
          }
        }

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('UPLOAD FAILURE!!', error)
        throw error
      }
    },
    async massiveTemplateBlock ({ commit }, taxId) {
      try {
        const response = await axios.get(
          `${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/user/massive-blockUsers-template/${taxId}`,
          {
            responseType: 'blob',
            headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          }
        )

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        let fileName = 'Users_Block_Template_' + new Date().toISOString().slice(0, 10) + '.xlsx'
        if (response.headers['content-disposition']) {
          const parts = response.headers['content-disposition'].split('filename=')
          if (parts.length > 1) {
            fileName = parts[1].replace(/"/g, '')
          }
        }

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('UPLOAD FAILURE!!', error)
        throw error
      }
    },
    async massiveUploadBlockUsers ({ commit }, formData) {
      try {
        const response = await axios.post(
          `${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/user/massive-blockUsers-process`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        return response
      } catch (e) {
        console.error('UPLOAD FAILURE!!', e)
        throw e
      }
    },
    async massiveUploadUsers ({ commit }, formData) {
      try {
        const response = await axios.post(
          `${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/user/massive-upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        return response
      } catch (e) {
        console.error('UPLOAD FAILURE!!', e)
        throw e
      }
    },
    async createMarketplaceEnterprise ({ commit }, { step, supplier }) {
      try {
        if (!validateDataAreComplete(supplier, 'company')) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, 'Datos de la empresa proveedora incompletos. Por favor, complete los campos obligatorios (*).')
        }

        const payload = {
          TaxId: supplier.tax_id,
          Name: supplier.company_name,
          LegalName: supplier.company_legal_name,
          Phone: supplier.company_phone,
          URL: supplier.company_web_site_url,
          CommercialBusiness: supplier.company_business,
          TimeZoneId: supplier.marketplace_timezone_id,
          IsLocked: false,
          hasSignedTerms: false,
          IsLockedCAT4MRO: false
        }

        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/company`
        const { data } = await axios.post(url, payload)

        if (!data.created) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, data.message)
        }

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `TAX ID ${supplier.tax_id} encontrado en Marketplace UNILINK como "${supplier.company_name}".`)
      } catch (error) {
        console.error('Error creating marketplace enterprise:', error)
        return updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Fallo al crear: ${error.message}`)
      }
    },
    async createCat4mroOrganization ({ commit, state, dispatch }, { step, supplier, hasTraceability }) {
      try {
        if (!validateDataAreComplete(supplier, 'company') && hasTraceability) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, 'Datos de la empresa proveedora incompletos. Por favor, complete los campos obligatorios (*).')
        }

        if (!state.miningTaxonomies || state.miningTaxonomies.length === 0) {
          await dispatch('fetchMiningTaxonomies')
        }

        const latestTaxonomy = state.miningTaxonomies.reduce((latest, current) => {
          const latestNum = parseFloat(latest.name.toLowerCase().replace('v', ''))
          const currentNum = parseFloat(current.name.toLowerCase().replace('v', ''))
          return currentNum > latestNum ? current : latest
        })

        if (!state.publicCatalogTypes || state.publicCatalogTypes.length === 0) {
          await dispatch('fetchPublicCatalogTypes')
        }

        const catalogType = state.publicCatalogTypes[0]
        if (!latestTaxonomy || !catalogType) {
          await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, 'No se encontraron taxonomías mineras o tipos de catálogo publico validos')
          return { success: false, message: 'No se encontraron taxonomías mineras o tipos de catálogo publico validos' }
        }

        const country = state.countries.find(c => c.name === supplier.country)

        const catalogPayload = {
          name: `Catálogo ${supplier.company_name}`.toUpperCase(),
          is_active: true,
          is_default: true,
          taxonomy_id: latestTaxonomy.id,
          catalog_type_id: catalogType.id
        }

        const dateNow = new Date().toISOString()

        const enterprisePayload = {
          name: supplier.company_name,
          tax_id: supplier.tax_id,
          contact_name: supplier.contact_name,
          contact_email: supplier.contact_email,
          city: null,
          country: country ? country.code : null,
          address: supplier.company_address,
          created_at: dateNow,
          created_date: dateNow,
          last_billing: null,
          description: supplier.company_business,
          plan_id: supplier.acuseller_plan_type_id,
          website: supplier.company_web_site_url,
          requires_terms_acceptance: true,
          client_type: supplier.customer_type,
          legal_name: supplier.company_legal_name,
          contact_phone: supplier.contact_phone,
          newProvider: true,
          contract_start_date: supplier.contract_start_date,
          contract_end_date: supplier.contract_end_date,
          is_blocked_by_contract: null,
          entity_type: 1,
          catalogs: { data: catalogPayload }
        }

        await apolloClient.mutate({
          mutation: createCat4mroOrganization,
          variables: { data: enterprisePayload }
        })

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `TAX ID ${supplier.tax_id} se ha encontrado en ACCUSELLER como "${supplier.company_name}".`)
      } catch (error) {
        console.error('Error creating cat4mro enterprise:', error)
        return updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Fallo al crear: ${error.message}`)
      }
    },
    async createMarketplaceUser ({ commit }, { step, supplier, isLegal = false, hasTraceability }) {
      try {
        if (!validateDataAreComplete(supplier, `${isLegal ? 'contact_legal' : 'contact'}`) && hasTraceability) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Datos del ${isLegal ? 'representante legal' : 'usuario de la plataforma'} incompletos. Por favor, complete los campos obligatorios (*).`)
        }

        const payload = {
          Email: isLegal ? supplier.contact_legal_email : supplier.contact_email,
          FirstName: isLegal ? supplier.contact_legal_name : supplier.contact_name,
          LastName: isLegal ? supplier.contact_legal_lastname : supplier.contact_lastname,
          Phone: isLegal ? supplier.contact_legal_phone : supplier.contact_phone,
          EnterpriseTaxId: supplier.tax_id,
          RoleIdentity: isLegal ? 'MRO_MANAGE_SUPPLIER' : 'MRO_SUPPLIER',
          ContactType: isLegal ? 'Representante Legal' : 'Vendedor'
        }

        payload.Email = payload.Email.toLowerCase()

        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users`
        const { data } = await axios.post(url, payload)

        if (!data.created) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, data.message)
        }

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `Email ${payload.Email} se encuentra registrado en Marketplace.`)
      } catch (error) {
        console.error('Error creating marketplace user:', error)
        return updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Fallo al crear: ${error.response ? error.response.data.message : error.message}`)
      }
    },
    async createCat4mroUser ({ commit }, { step, supplier, isLegal = false, hasTraceability }) {
      try {
        if (!validateDataAreComplete(supplier, `${isLegal ? 'contact_legal' : 'contact'}`) && hasTraceability) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Datos del ${isLegal ? 'representante legal' : 'usuario de la plataforma'} incompletos. Por favor, complete los campos obligatorios (*).`)
        }

        const payload = {
          Email: isLegal ? supplier.contact_legal_email : supplier.contact_email,
          FirstName: isLegal ? supplier.contact_legal_name : supplier.contact_name,
          LastName: isLegal ? supplier.contact_legal_lastname : supplier.contact_lastname,
          Phone: isLegal ? supplier.contact_legal_phone : supplier.contact_phone,
          EnterpriseTaxId: supplier.tax_id,
          Role: isLegal ? supplier.contact_legal_position : supplier.contact_position,
          RoleIdentity: isLegal ? 'MRO_MANAGE_SUPPLIER' : 'MRO_SUPPLIER',
          ContactType: isLegal ? 'Representante Legal' : 'Vendedor'
        }

        payload.Email = payload.Email.toLowerCase()

        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users/cat4mro`
        const { data } = await axios.post(url, payload)

        if (!data.created) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, data.message)
        }

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `Email ${payload.Email} se encuentra registrado en ACCUSELLER.`)
      } catch (error) {
        console.error('Error creating cat4mro user:', error)
        return updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Fallo al crear: ${error.response ? error.response.data.message : error.message}`)
      }
    },
    async updateEnterpriseName ({ commit }, { step, supplier }) {
      try {
        const payload = {
          TaxId: supplier.tax_id,
          Name: supplier.company_name
        }

        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/company`
        const { data } = await axios.put(url, payload)

        if (!data.updated) {
          return await updateStep(step, VALIDATION_STATUS.SYSTEM_ERROR, data.message)
        }

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `TAX ID ${supplier.tax_id} encontrado en Marketplace UNILINK como "${supplier.company_name}".`)
      } catch (error) {
        console.error('Error updating marketplace user:', error)
        return updateStep(step, VALIDATION_STATUS.SYSTEM_ERROR, `Fallo al actualizar: ${error.message}`)
      }
    },
    async updateOrganizationName ({ commit }, { step, supplier }) {
      try {
        await apolloClient.mutate({
          mutation: updateOrganizationName,
          variables: { taxId: supplier.tax_id, name: supplier.company_name }
        })

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `TAX ID ${supplier.tax_id} se ha encontrado en ACCUSELLER como "${supplier.company_name}".`)
      } catch (error) {
        console.error('Error updating organization name:', error)
        return updateStep(step, VALIDATION_STATUS.SYSTEM_ERROR, `Fallo al actualizar: ${error.message}`)
      }
    },
    async sendCredentials ({ commit }, { email, step }) {
      try {
        const urlGetUser = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users/verify`
        const { data } = await axios.get(urlGetUser, { params: { email } })

        const urlSendCredentials = `${config.ENDPOINTS.HOOK_API}/api/mail/create`
        const payload = {
          TypeId: 903, // TypeId del formato de correo para credenciales,
          KeyId: data.Id
        }

        await axios.post(urlSendCredentials, payload)

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `Credenciales enviadas al email ${email}.`)
      } catch (error) {
        console.error('Error sending credentials:', error)
        return updateStep(step, VALIDATION_STATUS.PENDING, `Fallo al enviar credenciales: HTTP Status ${error.response.status}: ${error.response.data.message}`)
      }
    },
    async resetUserCredentials ({ commit }, { email, step }) {
      try {
        const urlGetUser = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users/verify`
        const { data } = await axios.get(urlGetUser, { params: { email } })

        const urlReset = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users/resetCredentials`
        const { data: resetData } = await axios.put(urlReset, { userId: data.Id })

        return resetData
      } catch (error) {
        console.error('Error resetting credentials:', error)
        return {
          reset: false,
          message: error.response ? error.response.data.message : error.message
        }
      }
    },
    async sendUserCredentials ({ commit }, idUser) {
      try {
        const urlSendCredentials = config.ENDPOINTS.HOOK_API
        const payload = {
          TypeId: 903, // TypeId del formato de correo para credenciales,
          KeyId: idUser
        }

        const result = await axios.post(urlSendCredentials, payload)

        return result
      } catch (error) {
        console.error('Error sending credentials:', error)
        return error
      }
    },
    async unlockMarketplaceEnterprise ({ commit }, { supplier, step }) {
      try {
        const payload = {
          taxId: supplier.tax_id
        }
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/company/unlock`
        const { data } = await axios.put(url, payload)

        if (!data.unlocked) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, data.message)
        }

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `TAX ID ${supplier.tax_id} se encuentra HABILITADO en Marketplace UNILINK.`)
      } catch (error) {
        console.error('Error unlocking marketplace enterprise:', error)
        return updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Fallo al desbloquear: ${error.response ? error.response.data.message : error.message}`)
      }
    },
    async unlockCat4mroOrganization ({ commit }, { supplier, step }) {
      try {
        await apolloClient.mutate({
          mutation: updateOrganizationLocked,
          variables: { taxId: supplier.tax_id, locked: false }
        })

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `TAX ID ${supplier.tax_id} se encuentra ACTIVO en ACCUSELLER.`)
      } catch (error) {
        console.error('Error unlocking cat4mro organization:', error)
        return updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Fallo al desbloquear: ${error.response ? error.response.data.message : error.message}`)
      }
    },
    async unlockUser ({ commit }, { email, step }) {
      try {
        const payload = {
          email: email
        }
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users/unlock`
        const { data } = await axios.put(url, payload)

        if (!data.unlocked) {
          return await updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, data.message)
        }

        return await updateStep(step, VALIDATION_STATUS.SUCCESS, `Email ${email} se encuentra HABILITADO en Marketplace.`)
      } catch (error) {
        console.error('Error unlocking user:', error)
        return updateStep(step, VALIDATION_STATUS.VALIDATION_ERROR, `Fallo al desbloquear: ${error.response ? error.response.data.message : error.message}`)
      }
    },
    async fetchStatistics ({ commit }) {
      try {
        const now = new Date()

        const todayStart = new Date(now)
        todayStart.setHours(0, 0, 0, 0)

        const tomorrowStart = new Date(todayStart)
        tomorrowStart.setDate(todayStart.getDate() + 1)
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getSupplierStats,
          variables: {
            todayStart: todayStart.toISOString(),
            tomorrowStart: tomorrowStart.toISOString()
          }
        })

        const total = response.data.total.aggregate.count
        const today = response.data.today.aggregate.count
        const topSample = response.data.topSample || []

        const buyerCounts = {}
        for (const r of topSample) {
          const key = r.buyer_name || 'N/A'
          buyerCounts[key] = (buyerCounts[key] || 0) + 1
        }

        const topBuyer = Object.entries(buyerCounts).reduce((max, [buyer, count]) => {
          return count > max.count ? { buyer_name: buyer, count } : max
        }, { buyer_name: 'N/A', count: 0 })

        commit('SET_STATISTICS', {
          todayRecords: today,
          totalRecords: total,
          topBuyer
        })
      } catch (error) {
        console.error('Error fetching statistics:', error)
        throw error
      }
    },
    async fetchSupplierById ({ commit }, id) {
      try {
        const response = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: getSupplierById,
          variables: { id: Number(id) }
        })

        const list = (response && response.data && response.data.hub_supplier_register) || []
        const item = list[0] || null
        if (!item) return null

        const steps = (item.steps || []).reduce((acc, st) => {
          const key = st.step_type
          if (!acc[key]) acc[key] = st
          return acc
        }, {})
        const stepsList = Object.values(steps)

        let last = null
        for (const st of stepsList) {
          if (!last) { last = st; continue }
          const a = last.validated_at || ''
          const b = st.validated_at || ''
          if ((b && !a && b) || (a && b && b > a) || (!a && !b && st.id > last.id)) {
            last = st
          }
        }

        const generalStatus = last ? last.status : (item.finalized_at ? 5 : 0)
        const generalStatusMessage = last ? (last.message || '') : (item.finalized_at ? 'Finalizado' : 'Pendiente')

        let usersAssociate = null
        try {
          const url = config.ENDPOINTS.NEST_BACKEND + '/hub-suppliers/supplier-users/' + item.tax_id
          const usersResponse = await axios.get(url)
          usersAssociate = usersResponse.data || null
        } catch (e) {
          console.warn('No se pudo obtener users_associate:', e)
        }

        const normalized = {
          ...item,
          steps: stepsList,
          last_step_status: generalStatus,
          last_step_message: generalStatusMessage,
          general_status: generalStatus,
          general_status_message: generalStatusMessage,
          users_associate: usersAssociate || null
        }
        commit('SET_SELECTED_SUPPLIER', normalized)
        return normalized
      } catch (error) {
        console.error('Error fetching supplier by id:', error)
        return null
      }
    },
    async fetchValidationSteps ({ commit, state, dispatch }, { supplier, hasTraceability }) {
      // TODO: Refactor para remover mocks y usar data real, organizar steps y usar los nuevos obtenidos desde DB
      try {
        if (!state.stepsMeta || state.stepsMeta.length === 0) {
          await dispatch('fetchStepsMeta')
        }

        const stepsMeta = state.stepsMeta || []

        if (!supplier.steps) {
          return null
        }

        // Ordenar los steps usando order_to_show de stepsMeta
        const sortedSteps = supplier.steps.slice().sort((a, b) => {
          const metaA = stepsMeta.find(m => m.name === a.step_type)
          const metaB = stepsMeta.find(m => m.name === b.step_type)

          if (!metaA || !metaB) return 0
          return metaA.order_to_show - metaB.order_to_show
        })

        const mappedSteps = sortedSteps.map(step => {
          const meta = stepsMeta.find(m => m.name === step.step_type)
          return {
            id: step.id,
            step_type: step.step_type,
            title: meta ? meta.title : step.step_type.replace(/_/g, ' ').toUpperCase(),
            message: step.message || '',
            status: step.status || 0,
            validated_at: step.validated_at,
            not_traceability: step.not_traceability,
            isUnified: meta ? !!meta.is_unified : false
          }
        })

        const filteredSteps = []
        const taxAgg = {
          step_type: USAGE_STEPS_TYPES.tax_id_platform_creaction,
          title: AGGREGATED_TITLES[USAGE_STEPS_TYPES.tax_id_platform_creaction] || 'TaxID: Creación en plataforma',
          message: '',
          status: 5,
          validated_at: null
        }
        const legalAgg = {
          step_type: '',
          title: AGGREGATED_TITLES[USAGE_STEPS_TYPES.contact_legal_user_register] || 'Representante Legal: Registro en la plataforma',
          message: '',
          status: 5,
          validated_at: null
        }
        const nonLegalAgg = {
          step_type: '',
          title: AGGREGATED_TITLES[USAGE_STEPS_TYPES.contact_user_register] || 'Usuario: Registro en la plataforma',
          message: '',
          status: 5,
          validated_at: null
        }

        mappedSteps.forEach(step => {
          if (!step.isUnified) {
            filteredSteps.push(step)
            return
          }

          if (step.step_type.includes('tax_id')) {
            taxAgg.message += (step.message ? step.message + '\n' : '')
            if (step.status !== VALIDATION_STATUS.SUCCESS) {
              if (taxAgg.step_type !== 'tax_id_marketplace_validation') {
                taxAgg.step_type = step.step_type
                taxAgg.status = step.status
              }
            }
            if (
              !taxAgg.validated_at ||
              (step.validated_at && new Date(step.validated_at) > new Date(taxAgg.validated_at))
            ) {
              taxAgg.validated_at = step.validated_at
            }
            taxAgg.created_at = new Date().toISOString()
          } else if (step.step_type.includes('contact_legal_email')) {
            legalAgg.message += (step.message ? step.message + '\n' : '')
            if (step.status !== VALIDATION_STATUS.SUCCESS) {
              if (
                legalAgg.step_type !== USAGE_STEPS_TYPES.contact_legal_email_marketplace_validation ||
                step.step_type === USAGE_STEPS_TYPES.contact_legal_email_marketplace_validation
              ) {
                legalAgg.step_type = step.step_type
                legalAgg.status = step.status
              }
            }
            if (
              !legalAgg.validated_at ||
              (step.validated_at && new Date(step.validated_at) > new Date(legalAgg.validated_at))
            ) {
              legalAgg.validated_at = step.validated_at
            }
          } else if (step.step_type.includes('contact_email')) {
            nonLegalAgg.message += (step.message ? step.message + '\n' : '')
            if (step.status !== VALIDATION_STATUS.SUCCESS) {
              if (
                nonLegalAgg.step_type !== USAGE_STEPS_TYPES.contact_email_marketplace_validation ||
                step.step_type === USAGE_STEPS_TYPES.contact_email_marketplace_validation
              ) {
                nonLegalAgg.step_type = step.step_type
                nonLegalAgg.status = step.status
              }
            }
            if (
              !nonLegalAgg.validated_at ||
              (step.validated_at && new Date(step.validated_at) > new Date(nonLegalAgg.validated_at))
            ) {
              nonLegalAgg.validated_at = step.validated_at
            }
          }
        })

        const findIndex = type => filteredSteps.findIndex(s => s.step_type === type)
        const legalPos = hasTraceability ? findIndex(USAGE_STEPS_TYPES.contact_legal_email_sendgrid_validation) : ''
        const nonLegalPos = findIndex(USAGE_STEPS_TYPES.contact_email_sendgrid_validation)
        const taxPos = findIndex(USAGE_STEPS_TYPES.tax_id_gob_validation)

        const insertAt = (arr, idx, item) => {
          if (idx !== -1) arr.splice(idx + 1, 0, item)
          else arr.push(item)
        }

        insertAt(filteredSteps, legalPos, legalAgg)
        insertAt(filteredSteps, nonLegalPos, nonLegalAgg)
        insertAt(filteredSteps, taxPos, taxAgg)

        const getByType = type => mappedSteps.find(s => s.step_type === type) || { status: 0 }
        const stepsCount = mappedSteps.length

        let creationState = { can_create: true, required_creations: [], message: '' }
        if (stepsCount <= 4) {
          creationState = {
            can_create: false,
            required_creations: [
              USAGE_STEPS_TYPES.tax_id_marketplace_validation,
              USAGE_STEPS_TYPES.tax_id_cat4mro_validation,
              USAGE_STEPS_TYPES.contact_email_marketplace_validation,
              USAGE_STEPS_TYPES.contact_legal_email_marketplace_validation
            ],
            message: 'Debe crear la empresa en Marketplace y ACUSELLER antes de realizar esta acción'
          }
        } else if (getByType(USAGE_STEPS_TYPES.tax_id_validation).status !== 5 && hasTraceability) {
          creationState = {
            can_create: false,
            required_creations: [],
            message: 'Debe validar el formato del TaxID antes de realizar esta acción'
          }
        } else if (getByType(USAGE_STEPS_TYPES.tax_id_marketplace_validation).status !== 5) {
          creationState = {
            can_create: false,
            required_creations: [USAGE_STEPS_TYPES.tax_id_marketplace_validation],
            message: 'Debe crear la empresa en Marketplace antes de realizar esta acción'
          }
        } else if (getByType(USAGE_STEPS_TYPES.tax_id_cat4mro_validation).status !== 5) {
          creationState = {
            can_create: false,
            required_creations: [USAGE_STEPS_TYPES.tax_id_cat4mro_validation],
            message: 'Debe crear la empresa en ACUSELLER antes de realizar esta acción'
          }
        }

        const unnecessary = [
          USAGE_STEPS_TYPES.hubspot_integration,
          USAGE_STEPS_TYPES.tax_id_gob_validation,
          USAGE_STEPS_TYPES.tax_id_terms_validation,
          USAGE_STEPS_TYPES.contact_email_credentials_validation,
          USAGE_STEPS_TYPES.contact_email_credentials_notification,
          USAGE_STEPS_TYPES.contact_legal_email_credential_validation,
          USAGE_STEPS_TYPES.contact_legal_email_credential_notification
        ]
        const canSendCredentials = !mappedSteps.some(
          s => s.status !== 5 && !unnecessary.includes(s.step_type)
        )

        let unlockState = { can_unlock: false, unlock_disabled: [] }
        if (stepsCount <= 4) {
          // No se puede desbloquear si hay 4 o menos steps
        } else if (getByType(USAGE_STEPS_TYPES.tax_id_marketplace_validation).status !== 5) {
          unlockState.unlock_disabled.push(USAGE_STEPS_TYPES.tax_id_locked_marketplace_validation)
          unlockState.unlock_disabled.push(USAGE_STEPS_TYPES.contact_email_marketplace_locked_validation)
          unlockState.unlock_disabled.push(USAGE_STEPS_TYPES.contact_legal_email_marketplace_locked_validation)
        } else if (getByType(USAGE_STEPS_TYPES.contact_email_marketplace_validation).status !== 5) {
          unlockState.unlock_disabled.push(USAGE_STEPS_TYPES.contact_email_marketplace_locked_validation)
        } else if (getByType(USAGE_STEPS_TYPES.contact_legal_email_marketplace_validation).status !== 5) {
          unlockState.unlock_disabled.push(USAGE_STEPS_TYPES.contact_legal_email_marketplace_locked_validation)
        } else {
          unlockState.can_unlock = true
        }

        commit('SET_VALIDATION_STEPS', filteredSteps)
        commit('SET_CREATION_STATE', creationState)
        commit('SET_UNLOCK_STATE', unlockState)
        commit('SET_CAN_SEND_CREDENTIALS', canSendCredentials)

        return { steps: filteredSteps, creationState, unlockState, canSendCredentials }
      } catch (error) {
        console.error('Error fetching validation steps:', error)
        throw error
      }
    },
    async fetchSupplierOverview ({ commit }, taxId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/company/general-status`
        const { data } = await axios.get(url, { params: { taxId: taxId } })

        const overview = {
          marketplace_locked: false,
          cat4mro_locked: false,
          active_users: 0,
          locked_users: 0,
          termsAndConditions: [],
          historicalDocuments: [],
          users: [],
          companyName: {}
        }

        if (data) {
          const response = await apolloClient.query({
            fetchPolicy: 'network-only',
            query: getOrganizationTermsAndConditions,
            variables: { taxId: taxId }
          })
          const termsAndConditions = (response && response.data && response.data.organization_accepted_terms) || []

          overview.marketplace_locked = data.IsLockedMarketplace
          overview.cat4mro_locked = data.IsLockedCAT4MRO
          overview.active_users = data.TotalActiveUsers
          overview.locked_users = data.TotalLockedUsers
          overview.termsAndConditions = termsAndConditions
          overview.historicalDocuments = [] // TODO: Obtener el historico de documentos de T&C
          overview.companyName = data.CompanyName
          overview.users = data.Users.map(user => ({
            Id: user.Id,
            email: user.Email,
            first_name: user.Name,
            last_name: user.LastName,
            creation_date: user.CreatedDate,
            locked: user.IsBlocked,
            exists_cat4mro: user.ExistsInCAT4MRO,
            has_claims: user.HasClaims,
            claims: user.Claims
          }))
          commit('SET_SUPPLIER_OVERVIEW', overview)
          return overview
        }

        commit('SET_SUPPLIER_OVERVIEW', overview)
        return overview
      } catch (error) {
        console.error('Error fetching supplier overview:', error)
        throw error
      }
    },
    async finalizeSupplier ({ commit }, payload) {
      try {
        await apolloClient.mutate({
          mutation: finalizeSupplier,
          variables: { id: Number(payload.id), finalized_at: payload.finalized_at, finalized_by: payload.finalized_by }
        })
      } catch (error) {
        console.error('Error finalizing supplier:', error)
        throw error
      }
    },
    async createMarketplaceSingleUser ({ commit }, {user}) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users`
        const { data } = await axios.post(url, user)
        return data
      } catch (error) {
        console.error('Error creating marketplace user:', error)
      }
    },
    async createCat4mroSingleUser ({ commit }, { user }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/users/cat4mro`
        const { data } = await axios.post(url, user)

        return await data
      } catch (error) {
        console.error('Error creating cat4mro user:', error)
      }
    },
    async updateMarketplaceSingleUser ({ commit }, {user}) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/updateUser`
        const { data } = await axios.post(url, user)
        return data
      } catch (error) {
        console.error('Error creating marketplace user:', error)
      }
    },
    async updateCat4mroSingleUser ({ commit }, { user }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/updateUser/cat4mro`
        const { data } = await axios.post(url, user)

        return await data
      } catch (error) {
        console.error('Error creating cat4mro user:', error)
      }
    },
    async getUserDetailsById ({ commit }, userId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/userDetailsById/${userId}`
        const { data } = await axios.get(url)

        return await data
      } catch (error) {
        console.error('Error get user info:', error)
      }
    },
    async getUserCredentials ({ commit }, userId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/userCredentials/${userId}`
        const { data } = await axios.get(url)

        return await data
      } catch (error) {
        console.error('Error get user info:', error)
      }
    },
    async getUserRoles ({ commit }, userId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/userRoles/${userId}`
        const { data } = await axios.get(url)

        return data
      } catch (error) {
        console.error('Error get user info:', error)
      }
    },
    async getUserStatus ({ commit }, userId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/userStatusPlatforms/${userId}`
        const { data } = await axios.get(url)

        return data
      } catch (error) {
        console.error('Error get user info:', error)
      }
    },
    async updateCredentials ({ commit }, Credentials) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/updateCredentials`
        const { data } = await axios.post(url, Credentials)
        return data
      } catch (error) {
        console.error('Error update credentials:', error)
      }
    },
    async updateUserMarketplaceStatus ({ commit }, status) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/updateUserMarketplaceStatus`
        const { data } = await axios.post(url, status)
        return data
      } catch (error) {
        console.error('Error update credentials:', error)
      }
    },
    async updateUserStatus ({ commit }, status) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/updateUserStatus`
        const { data } = await axios.post(url, status)
        return data
      } catch (error) {
        console.error('Error update credentials:', error)
      }
    },
    async disableWhatsappValidation ({ commit }, status) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/disableWhatsappValidation`
        const { data } = await axios.post(url, status)
        return data
      } catch (error) {
        console.error('Error update credentials:', error)
      }
    },
    async removeUser ({ commit }, userId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/removeUser`
        const { data } = await axios.post(url, { userId })
        return data
      } catch (error) {
        console.error('Error update credentials:', error)
      }
    },
    async validateTaxID ({ commit }, validateCompany) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/validateTaxID`
        const { data } = await axios.post(url, validateCompany)
        console.log(data)
        return data
      } catch (error) {
        console.error('Error validation taxid:', error)
      }
    },
    async saveUserPlatformAccess ({ commit }, { userId, applicationIds }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/Applications/user/${userId}`
        const { data } = await axios.post(url, { applicationIds })
        return data
      } catch (error) {
        console.error('Error save user platform access:', error)
      }
    },
    async getPlatformAccessByUserId ({ commit }, userId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/Applications/user/${userId}`
        const { data } = await axios.get(url)
        return data
      } catch (error) {
        console.error('Error get platform access:', error)
      }
    },
    async getBuyersPlatforms ({ commit }, userId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/ListApplications`
        const { data } = await axios.get(url)
        console.log('Raw buyers platforms data from API:', data)
        return data
      } catch (error) {
        console.error('Error get buyers platforms:', error)
      }
    },
    async associateBuyersUser ({ commit }, userId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/updateUserIdentity/${userId}`
        const { data } = await axios.post(url)

        return data
      } catch (error) {
        console.error('Error update user identity:', error)
      }
    },
    async associateUsersCAT4MRO ({ commit }, { taxId }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/associate-users-cat4mro`
        const { data } = await axios.post(url, { taxId })
        return data
      } catch (error) {
        console.error('Error associating users to CAT4MRO:', error)
        throw error
      }
    },
    async associateUserCAT4MRO ({ commit }, { userId, taxId }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/associate-user-cat4mro/${userId}`
        const { data } = await axios.post(url, { taxId })
        return data
      } catch (error) {
        console.error('Error associating users to CAT4MRO:', error)
        throw error
      }
    },
    async associateSupplierToBuyers ({ commit }, supplierId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/associateBuyer/${supplierId}`
        const { data } = await axios.post(url)

        return data
      } catch (error) {
        console.error('Error update user identity:', error)
      }
    },
    async fetchBuyerCompanies ({ commit }, { active, take = 10, skip = 0 } = {}) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/buyer-companies`

        const params = { take, skip }

        if (active !== undefined) {
          params.active = active
        }

        const { data } = await axios.get(url, { params })

        const list = Array.isArray(data.items) ? data.items : []

        commit('SET_BUYER_COMPANIES', list)
        commit('SET_BUYER_COMPANIES_TOTAL', data.total || 0)

        return data
      } catch (error) {
        console.error('Error fetching buyer companies:', error)
        commit('SET_BUYER_COMPANIES', [])
        commit('SET_BUYER_COMPANIES_TOTAL', 0)
        return { items: [], total: 0 }
      }
    },
    async createBuyerCompany ({ commit }, payload) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/buyer-company`

        const { data } = await axios.post(url, payload)

        if (data.data) {
          commit('ADD_BUYER_COMPANY', data.data)
        }

        return data
      } catch (error) {
        console.error('Error creating buyer company:', error)
        throw error
      }
    },
    async updateBuyerCompany ({ commit }, { id, payload }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/buyer-company/${id}`

        const { data } = await axios.put(url, payload)

        if (data.data) {
          commit('UPDATE_BUYER_COMPANY', data.data)
        }

        return data
      } catch (error) {
        console.error('Error updating buyer company:', error)
        throw error
      }
    },
    async activateBuyerCompany ({ commit }, id) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/buyer-company/${id}/activate`

        const { data } = await axios.put(url)

        if (data.data) {
          commit('UPDATE_BUYER_COMPANY', data.data)
        }

        return data
      } catch (error) {
        console.error('Error activating buyer company:', error)
        throw error
      }
    },
    async deactivateBuyerCompany ({ commit }, id) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/buyer-company/${id}/deactivate`

        const { data } = await axios.put(url)

        if (data.data) {
          commit('UPDATE_BUYER_COMPANY', data.data)
        }

        return data
      } catch (error) {
        console.error('Error deactivating buyer company:', error)
        throw error
      }
    },
    async fetchServicesByTaxId ({ commit }, taxId) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/company/${taxId}/services`
        const { data } = await axios.get(url)
        const list = Array.isArray(data) ? data : []
        commit('SET_COMPANY_SERVICES', list)
        return list
      } catch (error) {
        console.error('Error fetching services by taxId:', error)
        commit('SET_COMPANY_SERVICES', [])
        return []
      }
    },
    async fetchAllServices ({ commit, state }) {
      if (state.allServices && state.allServices.length) return state.allServices
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/services`
        const { data } = await axios.get(url)
        const list = Array.isArray(data) ? data : []
        commit('SET_ALL_SERVICES', list)
        return list
      } catch (error) {
        console.error('Error fetching all services:', error)
        commit('SET_ALL_SERVICES', [])
        return []
      }
    },
    async addServicesToCompany ({ commit }, { taxId, serviceIds }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/company/${taxId}/services`
        const { data } = await axios.post(url, { serviceIds })
        return data
      } catch (error) {
        console.error('Error adding services to company:', error)
        throw error
      }
    },
    async removeServiceFromCompany ({ commit }, { taxId, serviceId }) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/company/${taxId}/services/${serviceId}`
        const { data } = await axios.delete(url)
        return data
      } catch (error) {
        console.error('Error removing service from company:', error)
        throw error
      }
    },
    async validateBuyerCompaniesExcelStructure ({ commit }, formData) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/excel/validate-structure`
        const { data } = await axios.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        return data
      } catch (error) {
        console.error('Error validating excel structure:', error)
        throw error
      }
    },

    async uploadBuyerCompaniesExcel ({ commit }, formData) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/excel/process`
        const { data } = await axios.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        return data
      } catch (error) {
        console.error('Error processing buyer companies excel:', error)
        throw error
      }
    },

    async generateTemplate ({ commit }, template) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/excel/template`
        const { data } = await axios.post(url, template, {
          responseType: 'arraybuffer' // clave: evita que axios intente parsear el binario como texto/JSON
        })
        return data
      } catch (error) {
        console.error('Error processing buyer companies excel:', error)
        throw error
      }
    },
    async importBuyerCompaniesData ({ commit }, suppliers) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/invite-suppliers`
        const { data } = await axios.post(url, { suppliers })
        return data
      } catch (error) {
        console.error('Error importing suppliers:', error)
        throw error
      }
    },
    async validateSupplierContacts ({ commit }, payload) {
      try {
        const url = `${config.ENDPOINTS.NEST_BACKEND}/hub-suppliers/validate-contacts`
        const { data } = await axios.post(url, payload)
        return data
      } catch (error) {
        console.error('Error validating supplier contacts:', error)
        throw error
      }
    }
  },
  mutations: {
    SET_SUPPLIERS (state, data) {
      Vue.set(state, 'suppliers', data)
    },
    SET_SUPPLIERS_COUNT (state, data) {
      Vue.set(state, 'suppliersCount', data)
    },
    SET_STATISTICS (state, data) {
      Vue.set(state, 'statistics', data)
    },
    SET_BUYERS (state, data) {
      Vue.set(state, 'buyers', data)
    },
    SET_COUNTRIES (state, data) {
      Vue.set(state, 'countries', data)
    },
    SET_TIMEZONES (state, data) {
      Vue.set(state, 'timezones', data)
    },
    SET_PLAN_TYPES (state, data) {
      Vue.set(state, 'planTypes', data)
    },
    SET_CUSTOMER_TYPES (state, data) {
      Vue.set(state, 'customerTypes', data)
    },
    SET_VALIDATION_STEPS (state, data) {
      Vue.set(state, 'validationSteps', data)
    },
    SET_SUPPLIER_OVERVIEW (state, data) {
      Vue.set(state, 'supplierOverview', data)
    },
    SET_SELECTED_SUPPLIER (state, data) {
      Vue.set(state, 'selectedSupplier', data)
    },
    CLEAR_SELECTED_SUPPLIER (state) {
      Vue.set(state, 'selectedSupplier', null)
    },
    SET_CREATION_STATE (state, data) {
      Vue.set(state, 'creationState', data)
    },
    SET_UNLOCK_STATE (state, data) {
      Vue.set(state, 'unlockState', data)
    },
    SET_CAN_SEND_CREDENTIALS (state, data) {
      Vue.set(state, 'canSendCredentials', data)
    },
    SET_REGISTER_COUNTRIES_DISTINCT (state, data) {
      Vue.set(state, 'registerCountriesDistinct', data)
    },
    SET_STEPS_META (state, data) {
      Vue.set(state, 'stepsMeta', data)
    },
    SET_MINING_TAXONOMIES (state, data) {
      Vue.set(state, 'miningTaxonomies', data)
    },
    SET_PUBLIC_CATALOG_TYPES (state, data) {
      Vue.set(state, 'publicCatalogTypes', data)
    },
    APPEND_SUPPLIERS (state, data) {
      Vue.set(state, 'suppliers', [...state.suppliers, ...data])
    },
    SET_NEXT_CURSOR (state, data) {
      Vue.set(state, 'nextCursor', data)
    },
    SET_BUYER_COMPANIES (state, payload) {
      state.buyerCompanies = payload
    },
    SET_BUYER_COMPANIES_TOTAL (state, total) {
      state.buyerCompaniesTotal = total
    },
    ADD_BUYER_COMPANY (state, payload) {
      state.buyerCompanies.unshift(payload)
    },
    UPDATE_BUYER_COMPANY (state, payload) {
      const index = state.buyerCompanies.findIndex(b => b.id === payload.id)
      if (index !== -1) {
        state.buyerCompanies.splice(index, 1, payload)
      }
    },
    SET_COMPANY_SERVICES (state, data) {
      Vue.set(state, 'services', data)
    },
    SET_ALL_SERVICES (state, data) {
      Vue.set(state, 'allServices', data)
    }
  },
  getters: {
    suppliersInfo: state => state.suppliers,
    suppliersCount: state => state.suppliersCount,
    statisticsInfo: state => state.statistics,
    buyersInfo: state => state.buyers,
    countriesInfo: state => state.countries,
    timezonesInfo: state => state.timezones,
    planTypesInfo: state => state.planTypes,
    customerTypesInfo: state => state.customerTypes,
    validationStepsInfo: state => state.validationSteps,
    supplierOverviewInfo: state => state.supplierOverview,
    selectedSupplierInfo: state => state.selectedSupplier,
    creationStateInfo: state => state.creationState,
    unlockStateInfo: state => state.unlockState,
    canSendCredentialsInfo: state => state.canSendCredentials,
    stepsMetaInfo: state => state.stepsMeta,
    miningTaxonomiesInfo: state => state.miningTaxonomies,
    publicCatalogTypesInfo: state => state.publicCatalogTypes,
    nextCursorInfo: state => state.nextCursor,
    buyerCompaniesInfo: state => state.buyerCompanies,
    buyerCompaniesTotalInfo: state => state.buyerCompaniesTotal,
    servicesInfo: state => state.services,
    allServicesInfo: state => state.allServices
  }
}

export default hubSuppliers



// WEBPACK FOOTER //
// ./src/store/modules/hubSuppliers.js